import React, {useEffect, useRef, useState} from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import TextField from '../../../../../components/form/textfield/textfield';
import Button from '../../../../../components/form/button/button';
import Loader from '../../../../../components/loader/loader';
import Confirmation from '../../../../../components/confirmation/confirmation';
import TaskMedia from '../task-details/task-media/task-media';
import ScoringGuide from '../../components/scoring-guide/scoringGuide';
import ActionRequired from '../event-details/action-require';
import {crumbHref} from '../../utils/breadcrumb';
import {
  getDevice,
  saveDevice,
  getScoringReference,
  getTaskDetail,
  updateTaskMedia,
  deleteTaskMedia,
} from '../../../../../redux/risk-assessment-core/riskAssessmentCoreSlice';
import {
  getDeviceState,
  getRiskAssessmentCoreLoadingState,
  getScoringReferenceState,
} from '../../../../../redux/selectors/riskAssessmentCore';
import {getLoginStateSelector} from '../../../../../redux/selectors/loginSelector';
import {getUserRoleName, taskStatusEnum} from '../../../../../utils/constants';
import {getDateInFormat} from '../../../../../utils/DateUtils';
import {RiskChip, SeverityBadge} from '../../../../../components/risk-chip/riskChip';
import {
  deriveEvaluation,
  factorScore,
  impactLabel,
  IMPACT_STOP,
  FEASIBILITY_STOP,
  RISK_LEVEL_STOP,
} from '../../../../../utils/cybersecurityScoring';
import './device-details.scss';

function InfoTooltip({title}) {
  return (
    <Tooltip title={title} arrow placement="right" classes={{tooltip: 'criteria-tooltip-popper'}}>
      <button type="button" className="info-trigger" aria-label="Show scoring criteria">
        i
      </button>
    </Tooltip>
  );
}

function Guidance({guidance, guidanceItems}) {
  if (!guidance && !guidanceItems?.length) {
    return null;
  }
  return guidanceItems?.length > 0 ? (
    <ul className="criteria-tooltip__guidance-list">
      {guidanceItems.map(item => (
        <li key={item.level}>
          <strong>{item.level}</strong>: {item.text}
        </li>
      ))}
    </ul>
  ) : (
    <p className="criteria-tooltip__guidance">{guidance}</p>
  );
}

function ImpactCriteriaContent({category, reference}) {
  const hasExamples = category.examples && Object.keys(category.examples).length > 0;
  return (
    <div className="criteria-tooltip">
      <Guidance guidance={category.guidance} guidanceItems={category.guidance_items} />
      <table>
        <thead>
          <tr>
            <th>Level</th>
            <th>Criteria</th>
            {hasExamples && <th>Examples</th>}
          </tr>
        </thead>
        <tbody>
          {reference.impact_levels.map(level => (
            <tr key={level}>
              <th>{impactLabel(reference, level)}</th>
              <td>{category.criteria?.[level] ?? ''}</td>
              {hasExamples && <td>{category.examples?.[level] ?? ''}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FactorCriteriaContent({factor}) {
  const hasCriteria = factor.criteria && Object.keys(factor.criteria).length > 0;
  const hasExamples = factor.examples && Object.keys(factor.examples).length > 0;
  return (
    <div className="criteria-tooltip">
      <Guidance guidance={factor.guidance} guidanceItems={factor.guidance_items} />
      <table>
        <thead>
          <tr>
            <th>Option</th>
            <th>Score</th>
            {hasCriteria && <th>Criteria</th>}
            {hasExamples && <th>Examples</th>}
          </tr>
        </thead>
        <tbody>
          {factor.options.map(option => (
            <tr key={option.code}>
              <td>{option.label}</td>
              <td>{option.score}</td>
              {hasCriteria && <td>{factor.criteria?.[option.code] ?? ''}</td>}
              {hasExamples && <td>{factor.examples?.[option.code] ?? ''}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// TextField forwards its ref to the textarea, so the height can track the
// content without touching the shared component
function NotesField({name, value, readOnly, label = 'Notes', rows = 1, placeholder}) {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [value]);

  return (
    <TextField
      ref={ref}
      type="text"
      name={name}
      label={label}
      isMultiline
      rows={rows}
      placeholder={placeholder}
      styles={{height: 'auto'}}
      textAreaStyle={{width: '100%', resize: 'none', overflow: 'hidden', padding: '8px 10px'}}
      disabled={readOnly}
    />
  );
}

function ImpactRow({category, reference, values, handleChange, handleBlur, readOnly}) {
  const fieldName = `${category.key}_impact`;
  return (
    <div className="field-row">
      <span className="field-row__label-group">
        <label className="field-row__label" htmlFor={fieldName}>
          {category.label}
        </label>
        <InfoTooltip title={<ImpactCriteriaContent category={category} reference={reference} />} />
      </span>
      <div className="field-row__control">
        <select
          className="field-row__select"
          id={fieldName}
          data-stop={IMPACT_STOP[values[fieldName]]}
          name={fieldName}
          value={values[fieldName] ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={readOnly}
        >
          <option value="">Not set</option>
          {reference.impact_levels.map(level => (
            <option key={level} value={level}>
              {impactLabel(reference, level)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function FeasibilityRow({factor, reference, values, handleChange, handleBlur, readOnly}) {
  const score = factorScore(reference, factor.key, values[factor.key]);
  return (
    <div className="field-row">
      <span className="field-row__label-group">
        <label className="field-row__label" htmlFor={factor.key}>
          {factor.label}
        </label>
        <InfoTooltip title={<FactorCriteriaContent factor={factor} />} />
      </span>
      <div className="field-row__control">
        <select
          className="field-row__select"
          id={factor.key}
          name={factor.key}
          value={values[factor.key] ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={readOnly}
        >
          <option value="">Not set</option>
          {factor.options.map(option => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
        <span className={`field-row__score ${score === null ? 'field-row__score--muted' : ''}`}>
          {score === null ? '—' : score}
        </span>
      </div>
    </div>
  );
}

function RiskMatrixContent({reference, derived}) {
  return (
    <div className="risk-matrix-tooltip">
      <p className="risk-matrix-tooltip__summary">
        Most severe impact <strong>{derived.most_severe_impact_label}</strong> × feasibility{' '}
        <strong>{derived.feasibility_rating}</strong> {'→'}{' '}
        <strong>
          {derived.risk_value} {derived.risk_level}
        </strong>
      </p>
      <table className="risk-matrix-tooltip__table">
        <colgroup>
          <col className="risk-matrix-tooltip__col--axis-vertical" />
          <col className="risk-matrix-tooltip__col--axis" />
          {reference.feasibility_ratings.map(rating => (
            <col key={rating} className="risk-matrix-tooltip__col--rating" />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="is-blank" />
            <th className="is-blank" />
            <th colSpan={reference.feasibility_ratings.length}>Attack Feasibility Rating</th>
          </tr>
          <tr>
            <th className="is-blank" />
            <th className="is-blank" />
            {reference.feasibility_ratings.map(rating => (
              <th key={rating}>{rating}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reference.impact_levels.map((level, index) => (
            <tr key={level}>
              {index === 0 && (
                <th className="is-axis is-axis-vertical" rowSpan={reference.impact_levels.length}>
                  Impact Evaluation
                </th>
              )}
              <th className="is-axis">{impactLabel(reference, level)}</th>
              {reference.feasibility_ratings.map(rating => {
                const value = reference.risk_matrix?.[level]?.[rating];
                const isActiveCell =
                  level === derived.most_severe_impact && rating === derived.feasibility_rating;
                return (
                  <td key={rating} data-stop={isActiveCell ? RISK_LEVEL_STOP[value] : undefined}>
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultsPanel({reference, derived, values, readOnly}) {
  const chip = <RiskChip value={derived.risk_value} level={derived.risk_level} />;
  return (
    <div className="results-panel">
      <div className="results-panel__risk">
        <div className="results-panel__label">Overall risk</div>
        {derived.complete && reference ? (
          <Tooltip
            title={<RiskMatrixContent reference={reference} derived={derived} />}
            arrow
            placement="left"
            classes={{tooltip: 'risk-matrix-tooltip-popper'}}
          >
            <span className="results-panel__chip-trigger" tabIndex={0}>
              {chip}
            </span>
          </Tooltip>
        ) : (
          chip
        )}
        {!derived.complete && (
          <p className="results-panel__hint">
            Overall risk and the response policy are derived from all nine inputs.
          </p>
        )}
      </div>
      {derived.complete && (
        <div className="results-panel__policy">
          <div className="results-panel__block">
            <div className="results-panel__block-label">Response policy</div>
            <div className="results-panel__block-body">{derived.response_policy}</div>
          </div>
          <div className="results-panel__block">
            <div className="results-panel__block-label">Deliberation</div>
            <div className="results-panel__block-body">{derived.deliberation}</div>
          </div>
          <div className="results-panel__comment">
            <NotesField
              name="policy_comment"
              value={values.policy_comment}
              readOnly={readOnly}
              label="Policy and Risk Response Comment"
              rows={3}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function DeviceDetails() {
  const nav = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const selectedDevice = location.state?.selectedDevice;
  const selectedRiskAssessment = location.state?.selectedRiskAssessment;

  const device = useSelector(getDeviceState());
  const cachedReference = useSelector(getScoringReferenceState());
  const reference = Array.isArray(cachedReference?.impact_categories) ? cachedReference : null;
  const isLoading = useSelector(getRiskAssessmentCoreLoadingState());
  const {loggedInUser} = useSelector(getLoginStateSelector());
  const readOnly = getUserRoleName(loggedInUser?.role) === 'general';

  const [showMedia, setShowMedia] = useState(false);
  const [deviceMedia, setDeviceMedia] = useState([]);
  const [showScoringGuide, setShowScoringGuide] = useState(false);
  const [hasUnsavedActionRows, setHasUnsavedActionRows] = useState(false);
  const [pendingSaveValues, setPendingSaveValues] = useState(null);

  useEffect(() => {
    if (!selectedDevice?.id) {
      nav(crumbHref['product-line']);
      return;
    }
    dispatch(getDevice({id: selectedDevice.id, callback: () => {}}));
  }, [selectedDevice?.id]);

  // always refetch: the slice is persisted, so a stale or malformed cached value would otherwise stick
  useEffect(() => {
    dispatch(getScoringReference({}));
  }, []);

  // state.device is persisted, so ignore it until it holds the device this page opened
  const currentDevice = device?.id === selectedDevice?.id ? device : null;

  // media belongs to the device's underlying task; reuse the task media pipeline
  useEffect(() => {
    if (!currentDevice?.task) {
      return;
    }
    dispatch(
      getTaskDetail({
        id: currentDevice.task,
        callback: resp => setDeviceMedia(resp?.media_details ?? []),
      }),
    );
  }, [currentDevice?.task]);

  useEffect(() => {
    if (deviceMedia?.length === 0) {
      setShowMedia(false);
    }
  }, [deviceMedia]);

  const handleMediaAdd = e => {
    const files = Array.from(e.target.files);
    const payload = new FormData();
    files.forEach(file => {
      payload.append('media', file);
    });
    dispatch(
      updateTaskMedia({
        id: currentDevice.task,
        payload,
        callback: resp => setDeviceMedia([...deviceMedia, ...resp]),
      }),
    );
  };

  const handleMediaDelete = media => {
    dispatch(
      deleteTaskMedia({
        id: currentDevice.task,
        payload: {media_id: media?.id},
        callback: () => setDeviceMedia(deviceMedia.filter(item => item?.id !== media?.id)),
      }),
    );
  };

  const initialValues = {
    name: currentDevice?.name ?? '',
    contactCentre: currentDevice?.contact_centre ?? '',
    contact: currentDevice?.contact ?? '',
    safety_impact: currentDevice?.safety_impact ?? '',
    financial_impact: currentDevice?.financial_impact ?? '',
    operational_impact: currentDevice?.operational_impact ?? '',
    privacy_impact: currentDevice?.privacy_impact ?? '',
    impact_note: currentDevice?.impact_note ?? '',
    required_time: currentDevice?.required_time ?? '',
    expertise: currentDevice?.expertise ?? '',
    knowledge_of_product: currentDevice?.knowledge_of_product ?? '',
    opportunity_for_attack: currentDevice?.opportunity_for_attack ?? '',
    equipment: currentDevice?.equipment ?? '',
    feasibility_note: currentDevice?.feasibility_note ?? '',
    policy_comment: currentDevice?.policy_comment ?? '',
    task_status: currentDevice?.task_status ?? 3,
  };

  const validate = Yup.object({
    name: Yup.string().trim().required('Device name is required'),
  });

  const handleSubmit = values => {
    if (hasUnsavedActionRows) {
      setPendingSaveValues(values);
      return;
    }
    saveDeviceValues(values);
  };

  const saveDeviceValues = values => {
    const payload = {
      name: values.name.trim(),
      contact_centre: values.contactCentre.trim(),
      contact: values.contact.trim(),
      safety_impact: values.safety_impact,
      financial_impact: values.financial_impact,
      operational_impact: values.operational_impact,
      privacy_impact: values.privacy_impact,
      impact_note: values.impact_note,
      required_time: values.required_time,
      expertise: values.expertise,
      knowledge_of_product: values.knowledge_of_product,
      opportunity_for_attack: values.opportunity_for_attack,
      equipment: values.equipment,
      feasibility_note: values.feasibility_note,
      policy_comment: values.policy_comment,
      task_status: Number(values.task_status),
    };
    dispatch(
      saveDevice({
        id: selectedDevice.id,
        payload,
        callback: () => {
          nav(crumbHref['risk-assessment'], {state: {...location.state}});
        },
      }),
    );
  };

  const impactCategories = reference?.impact_categories ?? [];
  const feasibilityFactors = reference?.feasibility_factors ?? [];

  return (
    <div className="device-details-wrapper">
      <div className="device-evaluation-top">
        <div className="details">
          <div className="heading">{currentDevice?.name ?? ''}</div>
        </div>
        <div className="device-evaluation-media-actions">
          {deviceMedia?.length > 0 && (
            <a onClick={() => setShowMedia(true)}>View Media</a>
          )}
          {!readOnly && (
            <div className="upload-media">
              <Button variant="secondary-button">Upload Media</Button>
              <input multiple type="file" onChange={handleMediaAdd} />
            </div>
          )}
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validate}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({values, handleChange, handleBlur}) => {
          const impacts = {};
          impactCategories.forEach(category => {
            impacts[category.key] = values[`${category.key}_impact`];
          });
          const factors = {};
          feasibilityFactors.forEach(factor => {
            factors[factor.key] = values[factor.key];
          });
          const derived = deriveEvaluation(reference, impacts, factors);

          return (
            <Form className="device-evaluation">
              <div className="device-evaluation-header">
                <TextField type="text" name="name" label="Device name" placeholder="Device name" disabled={readOnly} />
                <TextField
                  type="text"
                  name="contactCentre"
                  label="Contact center"
                  placeholder="Contact center"
                  disabled={readOnly}
                />
                <TextField type="text" name="contact" label="Contact" placeholder="Contact" disabled={readOnly} />
                <div className="device-status-field mb-4">
                  <div className="field-container">
                    <label htmlFor="task_status">Status</label>
                    <select
                      className="form-control shadow-none"
                      id="task_status"
                      name="task_status"
                      value={values.task_status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={readOnly}>
                      {taskStatusEnum.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <hr />

              <div className="device-evaluation-body">
                <div className="device-evaluation-scroll">
                  <section className="evaluation-section">
                    <div className="evaluation-section__header">
                      <span className="evaluation-section__title">Impact Evaluation</span>
                      <span className="evaluation-section__derived">
                        Most severe impact
                        <SeverityBadge label={derived.most_severe_impact_label} stop={IMPACT_STOP[derived.most_severe_impact]} />
                      </span>
                    </div>
                    <div className="evaluation-section__body">
                      <div className="evaluation-section__grid">
                        {impactCategories.map(category => (
                          <ImpactRow
                            key={category.key}
                            category={category}
                            reference={reference}
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>
                      <div className="evaluation-section__notes">
                        <NotesField name="impact_note" value={values.impact_note} readOnly={readOnly} />
                      </div>
                    </div>
                  </section>

                  <section className="evaluation-section">
                    <div className="evaluation-section__header">
                      <span className="evaluation-section__title">Attack Feasibility</span>
                      <span className="evaluation-section__derived">
                        Total
                        <span className="evaluation-section__total">
                          {derived.feasibility_total === null ? '—' : derived.feasibility_total}
                        </span>
                        <SeverityBadge
                          label={derived.feasibility_rating}
                          stop={FEASIBILITY_STOP[derived.feasibility_rating]}
                        />
                      </span>
                    </div>
                    <div className="evaluation-section__body">
                      <div className="evaluation-section__grid">
                        {feasibilityFactors.map(factor => (
                          <FeasibilityRow
                            key={factor.key}
                            factor={factor}
                            reference={reference}
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>
                      <div className="evaluation-section__notes">
                        <NotesField
                          name="feasibility_note"
                          value={values.feasibility_note}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                  </section>

                </div>

                <div className="device-evaluation-results">
                  <ResultsPanel reference={reference} derived={derived} values={values} readOnly={readOnly} />
                </div>
              </div>

              {currentDevice?.action_pue && (
                <div className="device-evaluation-actions">
                  <ActionRequired
                    eventId={currentDevice.action_pue}
                    readOnly={readOnly}
                    onEditingRowsChange={setHasUnsavedActionRows}
                    riskAssessmentId={selectedRiskAssessment?.id}
                    riskAssessmentName={selectedRiskAssessment?.name}
                    modelName={selectedRiskAssessment?.machine_detail?.map(el => el.name).join(', ')}
                    productLineName={selectedRiskAssessment?.product_detail?.name}
                    taskName={currentDevice?.name}
                    eventName={''}
                  />
                </div>
              )}

              <div className="device-evaluation-footer">
                <div className="device-evaluation-footer__saved">
                  {currentDevice?.scored_at && currentDevice?.scored_by_name && (
                    <span>
                      Last saved {getDateInFormat(new Date(currentDevice.scored_at), 'MM/DD/YYYY hh:mm A')} by{' '}
                      {currentDevice.scored_by_name}
                    </span>
                  )}
                </div>
                {!readOnly && (
                  <div className="device-evaluation-footer__actions">
                    <Button
                      type="button"
                      variant="secondary-button"
                      clickHandler={() => setShowScoringGuide(true)}
                    >
                      Scoring guide
                    </Button>
                    <Button type="submit" variant="submit-button">
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
      {isLoading && <Loader />}
      <Confirmation
        show={Boolean(pendingSaveValues)}
        setShow={() => setPendingSaveValues(null)}
        label="Unsaved action items"
        confirmationText="One or more action item rows have not been saved with Save Row and will be lost. Save the device anyway?"
        primaryButtonText="Save Anyway"
        secondaryButtonText="Go Back"
        primaryButtonClickHandler={() => {
          const values = pendingSaveValues;
          setPendingSaveValues(null);
          saveDeviceValues(values);
        }}
        secondaryButtonClickHandler={() => setPendingSaveValues(null)}
      />
      <ScoringGuide show={showScoringGuide} setShow={setShowScoringGuide} />
      {showMedia && (
        <TaskMedia
          medias={deviceMedia}
          show={showMedia}
          setShow={() => setShowMedia(false)}
          handleDelete={handleMediaDelete}
        />
      )}
    </div>
  );
}
